import { useEffect, useRef } from 'react';

import type { VisibleListState } from './types';

type VersionedIdentifiable = { id: number; version: number };
type PageMeta = { page: number };
type RealtimeEvent = { reason: string; itemId?: number };

export type RealtimeInfiniteListSyncOptions<
  TItem extends VersionedIdentifiable,
> = {
  userId?: number;
  visibleItems: Array<Pick<TItem, 'id' | 'version'>>;
  loadedPages: Array<{ meta: PageMeta }>;
  isListReady: boolean;
  listSessionKey: string;
  scopeChangeReasons: ReadonlySet<string>;
  additionalSyncReasons?: ReadonlySet<string>;
  createdReason: string;
  updatedReason: string;
  reassignedReason: string;
  deletedReason: string;
  subscribe: (listener: (event: RealtimeEvent) => void) => () => void;
  syncVisibleList: (params: {
    items: Array<Pick<TItem, 'id' | 'version'>>;
    loadedPages: number;
    refreshIds: number[];
    refreshProjection: boolean;
    signal: AbortSignal;
  }) => Promise<VisibleListState<TItem>>;
  captureViewportAnchor?: () => void;
  mergeVisibleState: (state: VisibleListState<TItem>) => boolean;
  replaceRealtimeRetained: (itemIds: number[]) => void;
  release: (itemId: number) => void;
  discardItemState: (itemId: number) => void;
  acknowledgeAddedItems: () => void;
  onItemsRemoved?: (itemIds: number[]) => void;
  syncDebounceMs?: number;
};

/**
 * Synchronize an infinite list against a server-ordered canonical window.
 * Scope-visible rows already shown by the client remain retained even when
 * they no longer match filters or move beyond the loaded canonical pages.
 */
export function useRealtimeInfiniteListSync<
  TItem extends VersionedIdentifiable,
>(options: RealtimeInfiniteListSyncOptions<TItem>) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const knownItemsRef = useRef<Array<Pick<TItem, 'id' | 'version'>>>([]);
  const knownItemsSessionRef = useRef('');
  if (knownItemsSessionRef.current !== options.listSessionKey) {
    knownItemsSessionRef.current = options.listSessionKey;
    knownItemsRef.current = options.isListReady ? options.visibleItems : [];
  } else if (options.isListReady) {
    const knownItemIds = new Set(knownItemsRef.current.map(({ id }) => id));
    const visibleItemsById = new Map(
      options.visibleItems.map((item) => [item.id, item]),
    );
    knownItemsRef.current = knownItemsRef.current.map(
      (item) => visibleItemsById.get(item.id) ?? item,
    );
    options.visibleItems.forEach((item) => {
      if (!knownItemIds.has(item.id)) {
        knownItemsRef.current.push(item);
      }
    });
  }
  const userId = options.userId;
  const listSessionKey = options.listSessionKey;

  useEffect(() => {
    let active = true;
    let syncTimer: ReturnType<typeof setTimeout> | null = null;
    let abortController: AbortController | null = null;
    let queue = Promise.resolve();
    let syncedLoadedPages = 0;
    const pendingRefreshIds = new Set<number>();
    let pendingProjectionRefresh = false;
    const initialOptions = optionsRef.current;
    if (
      initialOptions.listSessionKey !== listSessionKey ||
      initialOptions.userId !== userId
    ) {
      return;
    }

    const runSync = async () => {
      const context = optionsRef.current;
      const currentLoadedPages =
        context.loadedPages.at(-1)?.meta.page ?? context.loadedPages.length;
      syncedLoadedPages = Math.max(syncedLoadedPages, currentLoadedPages);
      if (!context.isListReady || syncedLoadedPages === 0) {
        return;
      }

      const requestSessionKey = context.listSessionKey;
      const previousItems = [...knownItemsRef.current];
      const refreshIds = Array.from(pendingRefreshIds);
      const refreshProjection = pendingProjectionRefresh;
      pendingRefreshIds.clear();
      pendingProjectionRefresh = false;
      abortController = new AbortController();
      let state: VisibleListState<TItem>;
      try {
        state = await context.syncVisibleList({
          items: previousItems,
          loadedPages: syncedLoadedPages,
          refreshIds,
          refreshProjection,
          signal: abortController.signal,
        });
      } catch {
        refreshIds.forEach((itemId) => {
          pendingRefreshIds.add(itemId);
        });
        pendingProjectionRefresh ||= refreshProjection;
        return;
      }
      if (!active || optionsRef.current.listSessionKey !== requestSessionKey) {
        return;
      }

      const previousIdSet = new Set(previousItems.map(({ id }) => id));
      context.captureViewportAnchor?.();
      if (!context.mergeVisibleState(state)) return;
      const versionsById = new Map(
        previousItems.map(({ id, version }) => [id, version]),
      );
      state.objects.forEach(({ id, version }) => {
        versionsById.set(id, version);
      });
      knownItemsRef.current = state.ids.flatMap((id) => {
        const version = versionsById.get(id);
        return version === undefined ? [] : [{ id, version }];
      });
      syncedLoadedPages = state.loadedPages;

      state.removedIds.forEach((itemId) => {
        context.discardItemState(itemId);
        context.release(itemId);
      });
      if (state.removedIds.length > 0) {
        context.onItemsRemoved?.(state.removedIds);
      }
      context.replaceRealtimeRetained(state.retainedIds);
      if (state.ids.some((itemId) => !previousIdSet.has(itemId))) {
        context.acknowledgeAddedItems();
      }
    };

    const enqueueSync = () => {
      queue = queue.catch(() => undefined).then(runSync);
      return queue;
    };

    const scheduleSync = () => {
      if (syncTimer !== null) clearTimeout(syncTimer);
      syncTimer = setTimeout(() => {
        syncTimer = null;
        void enqueueSync();
      }, optionsRef.current.syncDebounceMs ?? 75);
    };
    const itemReasons = new Set([
      initialOptions.createdReason,
      initialOptions.updatedReason,
      initialOptions.reassignedReason,
      initialOptions.deletedReason,
    ]);
    const unsubscribe = initialOptions.subscribe(({ itemId, reason }) => {
      if (
        !itemReasons.has(reason) &&
        !optionsRef.current.scopeChangeReasons.has(reason) &&
        !optionsRef.current.additionalSyncReasons?.has(reason)
      ) {
        return;
      }
      if (
        reason === optionsRef.current.updatedReason ||
        reason === optionsRef.current.reassignedReason
      ) {
        if (itemId === undefined) {
          pendingProjectionRefresh = true;
        } else {
          pendingRefreshIds.add(itemId);
        }
      }
      if (optionsRef.current.scopeChangeReasons.has(reason)) {
        pendingProjectionRefresh = true;
      }
      scheduleSync();
    });

    return () => {
      active = false;
      if (syncTimer !== null) clearTimeout(syncTimer);
      abortController?.abort();
      unsubscribe();
    };
  }, [listSessionKey, userId]);
}
